import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "../auth/useUser";
import { useTriggerRun } from "../auth/useTriggerRun";
import { Header, type HeaderMenuItem } from "../components/Header";
import { NextStepBar } from "../components/NextStepBar";
import { RunProgress } from "../components/RunProgress";
import { SpecColumn } from "../components/spec/SpecColumn";
import { reviewCardHref } from "../lib/reviewTarget";
import { ALLOWANCE_REFUSED_LINE, manualTriggerErrorLine, retryAfterPhrase } from "../lib/triggerCopy";
import { PAGE_LOAD_ERROR_LINE, TRY_AGAIN_ACT, actStartsAPass, barAct, type NextStep } from "../lib/nextStep";
import { IDEA_NOT_FOUND_LINE, specReadNotFound, type SpecView } from "../lib/specLines";
import { isRunInFlight, isTerminalRun, useGlobalRunStore } from "../hooks/useGlobalRunStore";
import { useRunInFlightRefusal } from "../hooks/useRunInFlightRefusal";
import { useQuestions, useDecisions } from "../hooks/useQueues";
import { passInFlight, useSpecFollow } from "../hooks/useSpecFollow";
import { useSpec } from "../hooks/useSpec";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { getDisplayName, showsIdeaLine } from "../lib/displayName";
import { EditableTitle } from "../components/EditableTitle";
import { usePatchProductTitle } from "../auth/usePatchProductTitle";
import type { TriggerError } from "../components/IdeaForm";

type SpecMeta = SpecView["meta"];

// API_BASE for direct <a download> navigation to export endpoints. The
// orchestrator validates cookie auth on these endpoints; the browser
// sends pmt_session automatically on a top-level navigation, so we don't
// need fetch + blob plumbing here. Mirrors the pattern in Login.tsx.
const API_BASE =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window !== "undefined" ? window.location.origin : "");

type LocationState = {
  streamUrl?: string;
  runId?: string;
  triggerError?: TriggerError;
};

export function Product() {
  const { productId } = useParams<{ productId: string }>();
  const { data: user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Run streaming is owned globally by <ActiveRunProvider> (0027 P1); the
  // Product view reads this product's run from the store rather than owning
  // the SSE connection, so the stream survives navigation away and back. The
  // store drives the first pass's takeover and, with the served step, the
  // in-flight rule that hides the layer reflections; what the page says about
  // the pass is the server's next step (0051 slice 2), never the store's
  // snapshot.
  const initial = (location.state ?? {}) as LocationState;
  const startRun = useGlobalRunStore((s) => s.startRun);
  const reviveRun = useGlobalRunStore((s) => s.reviveRun);
  const run = useGlobalRunStore((s) => (productId ? s.runs[productId] : undefined));
  const stream = run?.snapshot ?? null;

  // Auto-trigger error (e.g. 402 out-of-credits during idea submission)
  // is surfaced once on entry and then dismissed.
  const [autoTriggerError, setAutoTriggerError] = useState<TriggerError | undefined>(
    initial.triggerError,
  );

  // Auto-trigger path: IdeaForm passes streamUrl/runId via router state.
  // Register the run with the global store (the provider opens the stream),
  // then clear router state so a refresh doesn't re-seed.
  useEffect(() => {
    if (initial.streamUrl || initial.runId) {
      startRun(productId!, { streamUrl: initial.streamUrl, runId: initial.runId });
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 0051 slice 3: the page reads the spec read (hooks/useSpec), never /tree.
  // The next-step bar carries the live state, so the spec read does not
  // refetch on window focus. It sits under the tree key, so it still refetches
  // on every invalidation (a save, an act, a pass's end) and whenever the
  // served step's latest pass or state changes (hooks/useSpecFollow, the map's
  // rule too). A failed read carries its HTTP status, so the page tells a
  // missing idea from one that did not load.
  const specQuery = useSpec(productId!);

  const { step } = useSpecFollow(productId!);
  const stepState = step.data?.state;
  const stepRunId = step.data?.active_run_id;

  // 0051 slice 2: a cold load mid-pass follows the served step. When the step
  // says working and the store follows no live entry for its run, the page
  // revives that run (the provider's own rule), so the first pass's takeover
  // and the bar's working line show at once, not after the products list.
  // Keyed on the served values only, never the store's runs, and each served
  // run id is revived at most once: a client give-up on a run whose lease
  // stays live never loops; a newly served run id revives once.
  const revivedRunId = useRef<string | null>(null);
  useEffect(() => {
    if (!productId || stepState !== "working" || !stepRunId || stepRunId === revivedRunId.current) return;
    const store = useGlobalRunStore.getState();
    if (isRunInFlight(store.runs, productId) && store.runs[productId]?.runId === stepRunId) return;
    revivedRunId.current = stepRunId;
    store.reviveRun(productId, stepRunId);
  }, [productId, stepState, stepRunId]);

  const trigger = useTriggerRun(productId!);
  // 0051 slice 2: the bar's act stays pending from the click until the step
  // read that follows the trigger settles (success or error), so the cached
  // step's Continue never shows enabled again while the bar waits for the
  // pass the trigger started.
  const [starting, setStarting] = useState(false);
  const refetchStep = () =>
    queryClient
      .invalidateQueries({ queryKey: ["products", productId, "next-step"] })
      .finally(() => setStarting(false));

  const handleManualTrigger = () => {
    setStarting(true);
    trigger.mutate(undefined, {
      onSuccess: (data) => {
        startRun(productId!, { streamUrl: data.stream_url, runId: data.run_id });
        setAutoTriggerError(undefined);
        // 0051 slice 2: the bar reads the pass the trigger started.
        void refetchStep();
      },
      // 0159: a Run click that meets a live run joins it. The 409
      // trigger.run_in_flight names the run whose lease is live
      // (hints.run_id); reviveRun follows it, reviving an entry this tab read
      // as lost or interrupted (N6), and the bar reads the step again. No
      // error line renders for it. Any other refusal reads no step: the act
      // is no longer pending.
      onError: (err) => {
        const liveRunId = runInFlightRunId(err);
        if (liveRunId) {
          reviveRun(productId!, liveRunId);
          void refetchStep();
        } else {
          setStarting(false);
        }
      },
    });
  };

  // Cold-mount discovery (reload / dashboard-nav / fresh login during a
  // run) is handled globally by <ActiveRunProvider>, which scans the
  // product list for in-flight runs and seeds the store; this page also
  // follows its own served step (above).

  // On completion the store keeps the run's terminal snapshot for the run
  // menu and the toast; the bar reads the pass's result from the run record
  // through the next step (0051 slice 2). For a FIRST run, the takeover drops
  // on its own once the refetched spec read flips `analyzed` true (below).

  // useDocumentTitle must be called unconditionally before the
  // early-returns below; it falls back to "Eminity" when meta is
  // still loading.
  const displayName = specQuery.data ? getDisplayName(specQuery.data.meta) : null;
  useDocumentTitle(displayName);
  const patchTitle = usePatchProductTitle(productId ?? "");
  // 0028 edit gate: blocks editing only on the explicit
  // "placeholder" state (fresh products awaiting first ANALYZE).
  // Legacy products (pre-0028, empty title_source) are
  // grandfathered in — they're already past their first analysis.
  const titleSource = specQuery.data?.meta.title_source ?? "";
  const canEditTitle = titleSource !== "placeholder";
  // A run is in flight for this product (the store's snapshot is not
  // terminal). Hoisted above the breadcrumb: 0159 F1 hides the rename pencil
  // while it is.
  const isRunning = stream !== null && !isTerminalRun(stream);
  // 0159 F1: a stale tab's rename refused under a live run lease reads F1's
  // line only until the server's answer about this product's run changes.
  const titleRefusal = useRunInFlightRefusal(productId ?? "", isRunning);

  if (!user) return null;

  const namePreview = displayName
    ? truncate(displayName, 40)
    : "loading…";

  // Breadcrumb. EditableTitle wraps the title text so the founder
  // can rename in place once the first ANALYZE has authored an LLM
  // title (canEditTitle gates this — placeholder hides the ✎ icon).
  // The outer span carries the HTML title attr so hovering still
  // shows the full one-liner. has-[input]: drops the truncation
  // when the input is mounted so the founder isn't typing into a
  // 28ch-wide clipped box.
  const breadcrumb = (
    <>
      <span className="text-ink-600">/</span>
      <Link to="/" className="font-mono text-ink-400 hover:text-ink-200">ideas</Link>
      <span className="text-ink-600">/</span>
      <span
        className="font-mono text-ink-300 max-w-[28ch] inline-block has-[input]:max-w-none"
        title={specQuery.data?.meta.idea ?? ""}
      >
        {displayName ? (
          <EditableTitle
            title={displayName}
            canEdit={canEditTitle && !isRunning}
            onSave={(next) => patchTitle.mutateAsync(next)}
            inFlightRefusal={titleRefusal}
          />
        ) : (
          namePreview
        )}
      </span>
    </>
  );

  if (specQuery.isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} breadcrumb={breadcrumb} />
        <main className="flex-1 flex items-center justify-center text-sm text-ink-400 font-mono">
          loading…
        </main>
      </div>
    );
  }

  if (specQuery.error || !specQuery.data) {
    // 0051 slice 2: a 404 says the idea is not there; any other failure says
    // it did not load and offers Try again (specReadNotFound, the map's rule
    // too).
    const notFound = specReadNotFound(specQuery.error);
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            {notFound ? (
              <>
                <p className="font-mono text-xs text-ink-400 mb-2">404</p>
                <p className="text-ink-300">{IDEA_NOT_FOUND_LINE}</p>
              </>
            ) : (
              <>
                <p className="text-ink-300">{PAGE_LOAD_ERROR_LINE}</p>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => void specQuery.refetch()}
                    className="rounded-md border border-ink-700 px-3.5 py-1.5 text-xs text-ink-200 hover:border-ink-600 hover:text-ink-100 transition-colors"
                  >
                    {TRY_AGAIN_ACT}
                  </button>
                </div>
              </>
            )}
            <Link to="/" className="text-accent hover:text-accent-glow mt-4 inline-block">
              ← back to ideas
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const spec = specQuery.data;
  const meta = spec.meta;
  const analyzed = isAnalyzed(meta);

  // The fork (0027 P2): the FIRST run (empty product — nothing else to show)
  // takes over the page; this is the "watch the system work" moment, and it
  // also surfaces a first-run error. It drops on its own once the refetched
  // spec read flips `analyzed` true. Later passes never take over (0051 slice 2:
  // the bar says how the pass stands).
  if (stream && !analyzed) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} breadcrumb={breadcrumb} />
        <RunProgress
          stream={stream}
          title={getDisplayName(meta)}
          subtitle={meta.idea ?? undefined}
          productType={meta.product_type}
          onRetry={handleManualTrigger}
        />
      </div>
    );
  }

  // 0051 slice 2: a refused trigger says why - on the analyzed page under the
  // bar's act (the refused banner folds into the bar), on the not-started page
  // in its banner; a 409 trigger.run_in_flight joins the live run and says
  // nothing.
  const refusedTrigger = trigger.error && !runInFlightRunId(trigger.error) ? trigger.error : null;
  const triggerErrorLine = refusedTrigger ? manualTriggerErrorLine(refusedTrigger) : null;

  // 0051 slice 2: the analyzed page's own items in the header menu (the old
  // right rail's History, graph and exports).
  const exportBase = `${API_BASE}/api/v1/products/${productId}/export`;
  const menuItems: HeaderMenuItem[] | undefined = analyzed
    ? [
        { label: "History", to: `/products/${productId}/changes` },
        { label: "Map", to: `/products/${productId}/graph` },
        { label: "export.md", href: `${exportBase}/markdown`, download: true },
        { label: "export.json", href: `${exportBase}/json`, download: true },
      ]
    : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} breadcrumb={breadcrumb} menuItems={menuItems} />

      {autoTriggerError && (
        <TriggerErrorBanner error={autoTriggerError} onDismiss={() => setAutoTriggerError(undefined)} />
      )}
      {/* The not-started page (slice 4's) keeps today's banner, so a refused
          "Start first analysis" still says why. */}
      {!analyzed && refusedTrigger && <ManualTriggerErrorBanner error={refusedTrigger} />}

      {analyzed ? (
        <AnalyzedView
          productId={productId!}
          spec={spec}
          step={step.data}
          stepLoading={step.isLoading}
          stepError={step.isError}
          onRetryStep={() => void step.refetch()}
          onTrigger={handleManualTrigger}
          triggerPending={trigger.isPending || starting}
          running={isRunning}
          credits={user.credit_balance}
          triggerErrorLine={triggerErrorLine}
        />
      ) : (
        <PendingFirstAnalysisView
          meta={meta}
          userCredits={user.credit_balance}
          onTrigger={handleManualTrigger}
          triggerPending={trigger.isPending}
        />
      )}
    </div>
  );
}

// ---- Pending-first-analysis view ------------------------------------

function PendingFirstAnalysisView({
  meta,
  userCredits,
  onTrigger,
  triggerPending,
}: {
  meta: SpecMeta;
  userCredits: number;
  onTrigger: () => void;
  triggerPending: boolean;
}) {
  const noCredits = userCredits <= 0;
  return (
    <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-16">
      {/* Inner container keeps the long-form copy at a comfortable
          reading width (~3xl) while the outer max-w-6xl matches the
          chrome of the analyzed view + the rest of the app. */}
      <div className="max-w-3xl animate-slide-up">
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
          {meta.product_type ?? "idea"} · awaiting first analysis
        </p>
        <h1 className="text-display font-medium text-ink-50 tracking-tightest leading-[1.05]">
          {getDisplayName(meta)}
        </h1>
        {showsIdeaLine(meta) && (
          <p className="mt-3 text-[15px] text-ink-300 leading-relaxed max-w-2xl">
            {meta.idea}
          </p>
        )}

        <div className="mt-10 border-t border-ink-800 pt-8">
          <p className="text-[15px] text-ink-200 leading-[1.7] max-w-2xl">
            The first analysis turns this idea into a build-ready spec by
            surfacing the gaps you'll need to answer. About 9 minutes of LLM
            work.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={onTrigger}
              disabled={triggerPending || noCredits}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:bg-accent/40"
            >
              {triggerPending ? "starting…" : "Start first analysis"}
            </button>
            <span className="text-xs text-ink-400 font-mono">
              {noCredits ? ALLOWANCE_REFUSED_LINE : null}
            </span>
          </div>
        </div>

        <div className="mt-16">
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-4">
            What the first analysis builds
          </p>
          <ul className="space-y-3">
            {ANALYSIS_PREVIEW.map((item) => (
              <li key={item.label} className="flex items-baseline gap-3">
                <span className="font-mono text-[10px] text-ink-500 mt-0.5">
                  {item.label}
                </span>
                <span className="text-[13px] text-ink-300 leading-relaxed flex-1">
                  {item.body}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </main>
  );
}

const ANALYSIS_PREVIEW = [
  {
    label: "MOAT",
    body: "What about this idea is hard for a competitor to copy, and why a horizontal player can't simply add it as a feature.",
  },
  {
    label: "VALUE",
    body: "What concrete time, money, or anxiety this idea takes off the user — measured in hours/dollars, not slogans.",
  },
  {
    label: "EXPERIENCE",
    body: "What the user does in the first 30 seconds and the first week. The shape of the daily moment that matters.",
  },
  {
    label: "RISKS",
    body: "What would make a founder bounce off this idea — the abandonment triggers worth knowing before you start, not after.",
  },
];

// ---- Analyzed view ---------------------------------------------------

// Exported as the test seam for the Product tests (the next-step bar and the
// spec column over the served queues), as components/review/QuestionCard
// exports QuestionPanel for the Review tests.
export function AnalyzedView({
  productId,
  spec,
  step,
  stepLoading,
  stepError,
  onRetryStep,
  onTrigger,
  triggerPending,
  running,
  credits,
  triggerErrorLine,
}: {
  productId: string;
  spec: SpecView;
  // The served next step (undefined until it loads) and its read's state.
  step: NextStep | undefined;
  stepLoading: boolean;
  stepError: boolean;
  onRetryStep: () => void;
  // The bar's Continue / Try again.
  onTrigger: () => void;
  triggerPending: boolean;
  // True when the store follows a run in flight for this product. With the
  // served step's working state it hides the layer reflections at the
  // column's heads until the pass ends (0039 N4).
  running: boolean;
  credits: number;
  // A refused trigger's line, shown under the bar's act (lib/triggerCopy).
  triggerErrorLine: string | null;
}) {
  const questions = useQuestions(productId);
  const decisions = useDecisions(productId);
  // The settled line's target: each read opens that layer in the column and
  // scrolls its head into view (a new seq every time).
  const [reveal, setReveal] = useState<{ ring: number; seq: number }>();

  // 0167 N3: "take me to what waits on me" lands on it. The bar's "Review
  // them →" opens the card holding the first item the server's trigger gate
  // blocks a run on (the served trigger_blocking_node_ids, in the queue's
  // served order), not pos 0: a question when any blocks, else a decision
  // (0051 slice 2). The result's earlier-answer line opens the card holding
  // the first undrafted question awaiting the founder's covered-by ruling
  // (0164 R4). Each falls back to the first card (pos 0, as while the queues
  // load).
  const questionItems = questions.data?.items ?? [];
  const decisionItems = decisions.data?.items ?? [];
  const blockingQuestionIds = questions.data?.triggerBlockingNodeIds;
  const blockingDecisionIds = decisions.data?.triggerBlockingNodeIds;
  const isBlockingQuestion = (id: string) => blockingQuestionIds?.has(id) === true;
  const isBlockingDecision = (id: string) => blockingDecisionIds?.has(id) === true;
  const firstBlockingQuestionHref = reviewCardHref(productId, "questions", questions.data, isBlockingQuestion);
  const firstBlockingDecisionHref = reviewCardHref(productId, "decisions", decisions.data, isBlockingDecision);
  const reviewHref =
    !questionItems.some((n) => isBlockingQuestion(n.id)) && decisionItems.some((n) => isBlockingDecision(n.id))
      ? firstBlockingDecisionHref
      : firstBlockingQuestionHref;
  const coveredByIds = questions.data?.coveredByRulingNodeIds;
  const draftedQuestionIds = questions.data?.draftedNodeIds;
  const recapCheckHref = reviewCardHref(
    productId,
    "questions",
    questions.data,
    (id) => coveredByIds?.has(id) === true && draftedQuestionIds?.has(id) !== true,
  );

  // One column (0051 slices 2-3): the next-step bar first, then the spec
  // column. In flight (passInFlight, hooks/useSpecFollow: the store follows a
  // live run, or the served step says working), the column shows no layer
  // reflection and no settled mark (0039 N4: S1 commits layer closures CREATE
  // may reverse in the same pass).
  const inFlight = passInFlight(running, step?.state);

  // The result's settled line opens the column at the lowest layer the pass
  // closed: closed_layer_names are in layer order, so the first served layer
  // named among them. A button only while the column serves such a layer.
  const settledLayer = spec.layers.find(
    (l) => l.name !== "" && (step?.closed_layer_names ?? []).includes(l.name),
  );
  const readSettled = () => {
    if (settledLayer === undefined) return;
    const ring = settledLayer.ring;
    setReveal((r) => ({ ring, seq: (r?.seq ?? 0) + 1 }));
  };

  // The refused-Continue line when the page already knows nothing is left;
  // only under an act that would start a pass.
  const refusedLine = credits <= 0 && actStartsAPass(step ? barAct(step) : null) ? ALLOWANCE_REFUSED_LINE : null;

  return (
    <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-8 py-10 space-y-8">
      <NextStepBar
        step={step}
        loading={stepLoading}
        loadError={stepError}
        onRetryLoad={onRetryStep}
        productId={productId}
        reviewHref={reviewHref}
        checkHref={recapCheckHref}
        onAct={onTrigger}
        actPending={triggerPending}
        refusedLine={refusedLine}
        errorLine={triggerErrorLine}
        onReadSettled={readSettled}
        settledOpens={settledLayer !== undefined}
      />
      <SpecColumn
        productId={productId}
        spec={spec}
        questions={questions.data}
        decisions={decisions.data}
        inFlight={inFlight}
        reveal={reveal}
      />
    </main>
  );
}

// ---- Error banners --------------------------------------------------

function TriggerErrorBanner({
  error,
  onDismiss,
}: {
  error: TriggerError;
  onDismiss: () => void;
}) {
  const message =
    error.status === 402
      ? ALLOWANCE_REFUSED_LINE
      : error.status === 429
        ? `Rate limited. Try again in ${retryAfterPhrase(error.retryAfter)}.`
        : "Couldn't start the analysis automatically. You can start it manually below.";

  return (
    <div className="border-b border-warn-solid/30 bg-warn-solid/10">
      <div className="max-w-6xl mx-auto px-8 py-3 flex flex-wrap items-center justify-between gap-4 text-sm">
        <span className="text-warn-strong">{message}</span>
        <button
          onClick={onDismiss}
          className="font-mono text-xs text-warn-strong/70 hover:text-warn-strong"
        >
          dismiss
        </button>
      </div>
    </div>
  );
}

// runInFlightRunId reads a Run click's refusal that names the run already in
// flight (0159): a 409 trigger.run_in_flight whose error.hints.run_id names the
// run whose lease is live. The page joins that run (reviveRun) and shows no
// error line for it. Anything else is undefined.
function runInFlightRunId(err: unknown): string | undefined {
  const e = err as {
    status?: number;
    envelope?: { error?: { code?: string; hints?: { run_id?: unknown } } };
  } | null;
  if (!e || e.status !== 409 || e.envelope?.error?.code !== "trigger.run_in_flight") return undefined;
  const runId = e.envelope.error.hints?.run_id;
  return typeof runId === "string" && runId !== "" ? runId : undefined;
}

function ManualTriggerErrorBanner({ error }: { error: unknown }) {
  const message = manualTriggerErrorLine(error);

  return (
    <div className="border-b border-danger-solid/30 bg-danger-solid/10">
      <div className="max-w-6xl mx-auto px-8 py-3 text-sm text-danger-strong">{message}</div>
    </div>
  );
}

// ---- Sub-components --------------------------------------------------

function isAnalyzed(meta: SpecMeta): boolean {
  return !!(
    meta.moat ||
    meta.value_mechanism ||
    meta.core_experience ||
    meta.differentiation ||
    meta.abandonment_triggers
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}
